/** A dot grid: a `size` px dot, the same on screen at any zoom, every `step` world px at full detail. Color must be a literal. */
export type DotGrid = {
  step: number;
  size: number;
  color: string;
  opacity: number;
};

/** Screen px between dots below which every other dot has faded out; at `LEVEL_FULL` px and up they all show. */
const LEVEL_MIN = 12;
const LEVEL_FULL = 20;

export const smoothstep = (u: number) =>
  u <= 0 ? 0 : u >= 1 ? 1 : u * u * (3 - 2 * u);

/**
 * The grid at a zoom: `gap` screen px between the dots of the level showing, and `fine`, the opacity of the dots on an odd
 * row or column, which leave as the zoom falls. Each level is every 2nd, 4th... dot of the full grid, so a zoom never pops.
 */
export function dotGridLevel(grid: DotGrid, zoom: number) {
  if (!(zoom > 0)) return { gap: grid.step, fine: 1 };
  const doublings = Math.ceil(Math.log2(LEVEL_MIN / (grid.step * zoom)) - 1e-9);
  const gap = grid.step * 2 ** doublings * zoom;
  return {
    gap,
    fine: smoothstep((gap - LEVEL_MIN) / (LEVEL_FULL - LEVEL_MIN)),
  };
}

/**
 * CSS background for a grid off a React Flow plane, with dots where `DotGridBackground` draws them. Apply it as an inline
 * `style`: Chakra parses a `url()` holding a comma as a gradient and drops it.
 */
export function dotGridBackground(
  grid: DotGrid,
  { tx, ty, zoom }: { tx: number; ty: number; zoom: number } = {
    tx: 0,
    ty: 0,
    zoom: 1,
  },
) {
  const { gap, fine } = dotGridLevel(grid, zoom);
  const radius = grid.size / 2;
  const dot = (x: number, y: number, opacity: number) =>
    `<circle cx="${x + radius}" cy="${y + radius}" r="${radius}" fill-opacity="${opacity}"/>`;
  // One tile spans two gaps: its first dot is on the next level's grid, the other three fade with `fine`.
  const odd = grid.opacity * fine;
  const tile = `<svg xmlns="http://www.w3.org/2000/svg" width="${2 * gap}" height="${2 * gap}" fill="${grid.color}">${dot(0, 0, grid.opacity)}${odd > 0 ? dot(gap, 0, odd) + dot(0, gap, odd) + dot(gap, gap, odd) : ""}</svg>`;
  // Parentheses too, from a color function, so the url() reads the same quoted or not.
  const encoded = encodeURIComponent(tile)
    .replaceAll("(", "%28")
    .replaceAll(")", "%29");
  return {
    backgroundImage: `url("data:image/svg+xml,${encoded}")`,
    backgroundSize: `${2 * gap}px`,
    backgroundPosition: `${tx - radius}px ${ty - radius}px`,
  };
}
